import React, { useState, useRef, useEffect } from 'react';
import { ClickAwayListener, Theme, IconButton, Menu, MenuItem, Box, Chip, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getName, inspection, getAllPageLoadedSelector, getInspectionId, getInspectionSettings } from 'store';
import { isPrepUrl, isReportsUrl, isResultUrl } from 'utils/location';
import { useTracker } from '../Tracker/TrackerProvider';
import { makeStyles } from 'tss-react/mui';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LinkIcon from '@mui/icons-material/Link';
import { useQuery } from '@redux-requests/react';
import { fetchLinkedInspections } from 'store/request/inspections/actions';
import { InspectionStatuses } from 'types';
import { ApiLinkedInspection } from 'store/request/inspections/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { menuLightGrey } from 'styles/vars';
import styles from './ProjectName.module.css';

const useStyle = makeStyles()((theme: Theme) => ({
  projectNameContainer: {
    marginLeft: theme.spacing(8),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  linkedInspectionsButton: {
    padding: theme.spacing(0.5),
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'rgba(255, 255, 255, 1)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  linkedChip: {
    height: '20px',
    fontSize: '11px',
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    color: '#4caf50',
    '& .MuiChip-icon': {
      fontSize: '14px',
      color: '#4caf50',
    },
  },
  menuPaper: {
    backgroundColor: menuLightGrey,
    border: '1px solid var(--gds-color-cool-gray-450)',
    minWidth: '400px',
  },
  menuItem: {
    padding: theme.spacing(2),
    minHeight: '64px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
      '&:hover': {
        backgroundColor: 'rgba(76, 175, 80, 0.3)',
      },
    },
  },
}));

const START_CHARS_TO_KEEP = 30;
const END_CHARS_TO_KEEP = 10;

const ProjectName = () => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const nameRef = useRef<HTMLInputElement>(null);
  const { showLinkInspections } = useFlags();

  const [name, setname] = useState<string>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const inspectionName = useSelector(getName);
  const inspectionId = useSelector(getInspectionId);
  const inspectionSettings = useSelector(getInspectionSettings);
  const hasLoaded = useSelector(getAllPageLoadedSelector);
  const tracker = useTracker();
  const location = useLocation();
  const { isShared } = useCollaborators(inspectionId);

  const groupId = inspectionSettings?.groupId;
  const isLinked = Boolean(groupId);

  // Fetch linked inspections when groupId changes or when dropdown opens
  useEffect(() => {
    if (showLinkInspections && groupId) {
      dispatch(fetchLinkedInspections(groupId));
    }
  }, [showLinkInspections, groupId, dispatch]);

  // Read linked inspections from store using requestKey to get the correct cached data for this groupId
  const { data: linkedInspections } = useQuery<ApiLinkedInspection[]>({
    type: fetchLinkedInspections.toString(),
    requestKey: showLinkInspections && groupId ? `fetchLinkedInspections-${groupId}` : undefined,
  });

  useEffect(() => {
    setname(inspectionName);
  }, [inspectionName]);

  const handleNameChange = (value: string | null) => {
    setname(value ?? '');
  };

  const finishEditing = () => {
    if (nameRef.current) {
      nameRef.current.blur();
    }
  };

  const resetName = () => {
    setname(inspectionName);
    finishEditing();
  };

  const saveName = () => {
    if (name && name !== inspectionName) {
      dispatch(inspection.actions.setName(name));
      tracker.track({
        name: 'session-name-change',
        data: {
          name,
        },
      });
      finishEditing();
    } else resetName();
  };

  const handleNameKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveName();
    } else if (e.key === 'Escape') {
      resetName();
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const getTruncatedName = (inspectionName: string) => {
    if (!inspectionName) {
      return inspectionName;
    }
    // If the name is short enough, do not truncate
    if (inspectionName.length <= START_CHARS_TO_KEEP + END_CHARS_TO_KEEP) {
      return inspectionName;
    }
    return `${inspectionName.substring(0, START_CHARS_TO_KEEP)}...${inspectionName.substring(
      Math.max(0, inspectionName.length - END_CHARS_TO_KEEP),
    )}`;
  };

  const handleNavigateToInspection = (targetInspectionId: string, targetHasResults: boolean) => {
    if (targetInspectionId !== inspectionId) {
      // Navigate based on whether target inspection has results
      // If has results, go to results page; otherwise go to prep
      if (targetHasResults) {
        navigate(`/inspection/${targetInspectionId}/result`);
      } else {
        navigate(`/inspection/${targetInspectionId}`);
      }
    }
    handleCloseMenu();
  };

  if (!inspectionName || (!isPrepUrl(location) && !isReportsUrl(location) && !isResultUrl(location))) {
    return null;
  }

  const textField = (
    <GVTextField
      isDisabled={!hasLoaded || isShared}
      className={styles.field}
      ref={nameRef}
      id="projectName"
      label="Project name"
      isLabelHidden
      value={name}
      onValueChange={handleNameChange}
      onKeyUp={handleNameKeyUp}
      data-testid="project_name_field"
    />
  );

  return (
    <Box className={classes.projectNameContainer}>
      <ClickAwayListener onClickAway={saveName}>
        {isShared ? (
          <GVTooltip
            title="This project is shared and cannot be renamed. To rename it, remove all collaborators first."
            side="bottom"
          >
            {textField}
          </GVTooltip>
        ) : (
          textField
        )}
      </ClickAwayListener>

      {/* Linked Inspections Indicator & Dropdown */}
      {showLinkInspections && isLinked && linkedInspections && linkedInspections.length > 0 && (
        <>
          <Chip
            icon={<LinkIcon />}
            label={`${linkedInspections.length} Linked`}
            id="linked-inspections-indicator"
            size="small"
            className={classes.linkedChip}
          />
          <GVTooltip title="View linked inspections" side="bottom">
            <IconButton
              id="view-linked-inspections"
              className={classes.linkedInspectionsButton}
              size="small"
              onClick={handleOpenMenu}
              aria-label="View linked inspections"
            >
              <ArrowDropDownIcon />
            </IconButton>
          </GVTooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            PaperProps={{
              id: 'linked-inspection-dropdown-menu',
              className: classes.menuPaper,
            }}
          >
            {linkedInspections.map((linkedInsp) => {
              const truncatedName = getTruncatedName(linkedInsp.name);
              const needsTruncation = truncatedName !== linkedInsp.name;

              return (
                <GVTooltip
                  key={linkedInsp.id}
                  title={needsTruncation ? linkedInsp.name : ''}
                  side="left"
                  className={styles.tooltip}
                >
                  <MenuItem
                    onClick={() => handleNavigateToInspection(linkedInsp.id, Boolean(linkedInsp.lastJobId))}
                    id={`linked-inspection-dropdown-${linkedInsp.name}`}
                    selected={linkedInsp.id === inspectionId}
                    className={classes.menuItem}
                  >
                    <Box display="flex" flexDirection="column" width="100%" gap={0.5}>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {truncatedName}
                      </Typography>
                      <Box
                        display="flex"
                        data-testid={`linked-inspection-dropdown-${linkedInsp.name}`}
                        alignItems="center"
                        gap={1}
                      >
                        <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                          {linkedInsp.lastJobId ? InspectionStatuses.completed : linkedInsp.status.replace('_', ' ')}
                        </Typography>
                        {linkedInsp.id === inspectionId && (
                          <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                            • Current
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                </GVTooltip>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default ProjectName;
